/**
 * Styled components for SmartChoiceDemo
 */
import { styled } from '@uhg-abyss/web/tools/styled';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { PageBody } from '@uhg-abyss/web/ui/PageBody';
import { RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import { Text } from '@uhg-abyss/web/ui/Text';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { Toggle } from '@uhg-abyss/web/ui/Toggle';

// Main layout containers
export const Container = styled(Flex, {
  minHeight: '100vh',
  backgroundColor: '$core.color.neutral.10',
  flexDirection: 'column',
  // Desktop layout: sidebar + preview
  '@media (min-width: 900px)': {
    flexDirection: 'row',
  },
});

export const ControlPanel = styled('div', {
  width: '100%',
  maxWidth: '450px',
  padding: '20px',
  backgroundColor: 'white',
  borderRight: '1px solid $core.color.neutral.30',
  overflowY: 'auto',
  maxHeight: '100vh',
  boxSizing: 'border-box',
  // Keep sidebar pinned on desktop
  '@media (min-width: 900px)': {
    position: 'sticky',
    top: 0,
    height: '100vh',
  },
});

export const WidgetContainer = styled('div', {
  flex: 1,
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  boxSizing: 'border-box',
});

export const WidgetWrapper = styled('div', {
  backgroundColor: 'white',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  padding: '20px',
  width: '100%',
  maxWidth: '900px',
  boxSizing: 'border-box',
});

// Section components
export const Section = styled('div', {
  marginBottom: '28px',
  // subtle separator between sections (avoid multiple dividers)
  borderBottom: '1px solid $core.color.neutral.20',
  paddingBottom: '20px',
  // Do not show separator on last section in a container
  '&:last-of-type': {
    borderBottom: 'none',
    marginBottom: 0,
    paddingBottom: 0,
  },
});

export const SubSection = styled(Section, {
  backgroundColor: '#FAFAFA',
  borderRadius: '8px',
  padding: '20px',
  boxShadow: 'rgba(0, 0, 0, 0.05) 0px 1px 4px',
});

export const SectionTitle = styled(Text, {
  fontWeight: 'bold',
  marginBottom: '12px',
  color: '$core.color.brand.100',
  fontSize: '15px',
  display: 'block',
});

// Typography
export const StyledHeading = styled(Heading, {
  marginBottom: '16px',
});

export const StyledText = styled(Text, {
  marginBottom: '16px',
  color: '$core.color.neutral.60',
  fontSize: '14px',
});

export const SmallText = styled(Text, {
  fontSize: '12px',
  color: '$core.color.neutral.60',
  marginTop: '8px',
  display: 'block',
  lineHeight: 1.4,
});

export const StyledTextInput = styled(TextInput, {
  padding: '0 8px !important',
});

// Code display
export const PropsDisplay = styled('pre', {
  backgroundColor: '$core.color.neutral.20',
  padding: '16px',
  borderRadius: '6px',
  fontSize: '12px',
  overflow: 'auto',
  maxHeight: '200px',
  margin: 0,
});

// Form elements
export const FormField = styled('div', {
  marginBottom: '16px',
  padding: '16px',
  backgroundColor: '#FAFAFA',
  borderRadius: '8px',
  border: '1px solid $core.color.neutral.20',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    borderColor: '#929496',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  },
});

export const FormFieldLabel = styled(Text, {
  display: 'block',
  fontWeight: '600',
  marginBottom: '12px',
  color: '$core.color.neutral.90',
  fontSize: '14px',
});

// Styled Toggle with proper spacing
export const StyledToggleSwitch = styled(Toggle, {
  marginBottom: '4px',
  '& .abyss-toggle-root': {
    gap: '12px',
  },
  '& .abyss-toggle-label': {
    fontWeight: '$medium',
    marginLeft: '12px',
  },
  '& .abyss-toggle-sub-text': {
    marginLeft: '12px',
    marginTop: '4px',
  },
  // Ensure proper alignment
  '& .abyss-toggle-container': {
    alignItems: 'flex-start',
  },
});

// Radio group wrapper with proper spacing
export const RadioGroupWrapper = styled('div', {
  padding: '16px',
  backgroundColor: '#FAFAFA',
  borderRadius: '8px',
  border: '1px solid $core.color.neutral.20',
  marginBottom: '16px',
  // Label styling
  '& .abyss-radio-group-label': {
    fontWeight: '600',
    marginBottom: '16px',
    display: 'block',
    fontSize: '14px',
  },
  // Container for radio buttons
  '& .abyss-radio-group-container': {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  // Individual radio button styling
  '& .abyss-radio-root': {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  // Radio button circle
  '& .abyss-radio': {
    marginRight: '8px',
  },
  // Radio label text
  '& .abyss-radio-label': {
    marginLeft: '8px',
    fontSize: '14px',
    fontWeight: '$medium',
  },
  // Selected state
  '& input:checked ~ .abyss-radio-label': {
    fontWeight: 'bold',
  },
});

// Styled RadioGroup with proper spacing
export const StyledRadioGroup = styled(RadioGroup, {
  '.abyss-radio-group-container': {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    paddingTop: '8px',
  },
  '.abyss-radio-group-label': {
    fontWeight: '600',
    fontSize: '14px',
    marginBottom: '4px',
  },
});

// Styled Radio button with proper spacing
export const StyledRadio = styled(RadioGroup.Radio, {
  '.abyss-radio': {
    borderColor: '$core.color.brand.60',
    marginRight: '12px',
  },
  '.abyss-radio-label': {
    fontWeight: '$medium',
    fontSize: '14px',
  },
  'input:checked ~ .abyss-radio': {
    borderColor: '$core.color.brand.60',
  },
  'input:checked ~ label': {
    fontWeight: 'bold',
  },
});

// Divider with proper spacing
export const StyledDivider = styled(Divider, {
  margin: '28px 0',
  opacity: 0.9,
});

// Button groups
export const ButtonGroup = styled(Flex, {
  gap: '12px',
  flexWrap: 'wrap',
});

export const PresetButton = styled(Button, {
  fontSize: '12px',
});

export const PresetButtonGroup = styled(Flex, {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  flexWrap: 'wrap',
  marginBottom: '20px',
});

// Mock state section styling
export const MockStateSection = styled(PageBody, {
  backgroundColor: '$core.color.brand.5',
  border: '1px solid $core.color.brand.20',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '20px',
});

export const MockStateTitle = styled(Text, {
  fontWeight: 'bold',
  color: '#92400e',
  fontSize: '15px',
  marginBottom: '12px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const MockStateDescription = styled('div', {
  fontSize: '13px',
  color: '$core.color.neutral.80',
  marginBottom: '16px',
  lineHeight: '1.5',
});

// Subsection within MockStateSection
export const MockStateSubsection = styled('div', {
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
  border: '1px solid rgba(146, 64, 14, 0.2)',
  borderRadius: '6px',
  padding: '16px',
  marginBottom: '16px',
});

export const MockStateSubsectionTitle = styled(Text, {
  fontWeight: '600',
  color: '#78350f',
  fontSize: '13px',
  marginBottom: '12px',
  display: 'block',
});

// Persistence indicator
export const PersistenceIndicator = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 14px',
  backgroundColor: '#ecfdf5',
  border: '1px solid #6ee7b7',
  borderRadius: '6px',
  marginBottom: '20px',
  fontSize: '13px',
  color: '#065f46',
});

// Mock state summary in preview area
export const MockStateSummary = styled('div', {
  backgroundColor: '#fffbeb',
  border: '1px solid #fbbf24',
  borderRadius: '8px',
  padding: '12px 16px',
  marginBottom: '16px',
  maxWidth: '880px',
  width: '100%',
});

export const MockStateSummaryTitle = styled(Text, {
  fontWeight: '600',
  fontSize: '14px',
  color: '#92400e',
  marginBottom: '10px',
  display: 'block',
});

export const MockStateBadge = styled('span', {
  padding: '4px 10px',
  borderRadius: '4px',
  fontSize: '11px',
  fontFamily: 'monospace',
  variants: {
    active: {
      true: {
        backgroundColor: '#d1fae5',
        color: '#065f46',
      },
      false: {
        backgroundColor: '#fee2e2',
        color: '#991b1b',
      },
    },
  },
});

export const MockStateBadgeContainer = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
});

// Toggle group container for mock preferences
export const ToggleGroup = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  marginTop: '12px',
});

// Event Log Section (simulates Redux DevTools)
export const EventLogSection = styled('div', {
  marginTop: '20px',
  padding: '12px',
  backgroundColor: '#1e1e1e',
  borderRadius: '8px',
  border: '1px solid #333',
});

export const EventLogHeader = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '12px',
});

export const EventLogTitle = styled('h3', {
  fontSize: '14px',
  fontWeight: 600,
  color: '#10b981',
  margin: 0,
  fontFamily: 'monospace',
});

export const EventLogList = styled('div', {
  maxHeight: '300px',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

export const EventLogItem = styled('div', {
  padding: '10px 12px',
  backgroundColor: '#2d2d2d',
  borderRadius: '4px',
  borderLeft: '3px solid',
  fontFamily: 'monospace',
  fontSize: '12px',
  variants: {
    eventType: {
      PREFERENCES_LOADED: {
        borderLeftColor: '#3b82f6',
      },
      PREFERENCES_SAVED: {
        borderLeftColor: '#10b981',
      },
      PREFERENCES_ERROR: {
        borderLeftColor: '#ef4444',
      },
    },
  },
});

export const EventLogAction = styled('div', {
  color: '#fbbf24',
  fontWeight: 600,
  marginBottom: '4px',
});

export const EventLogTimestamp = styled('span', {
  color: '#6b7280',
  fontSize: '10px',
  marginLeft: '8px',
});

export const EventLogPayload = styled('pre', {
  color: '#9ca3af',
  fontSize: '11px',
  margin: '4px 0 0 0',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
  maxHeight: '100px',
  overflow: 'auto',
});

export const EventLogEmpty = styled('div', {
  color: '#6b7280',
  fontSize: '12px',
  fontStyle: 'italic',
  textAlign: 'center',
  padding: '20px',
});

export const ClearLogButton = styled('button', {
  padding: '6px 10px',
  fontSize: '12px',
  backgroundColor: '#374151',
  color: '#9ca3af',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontFamily: 'monospace',
  '&:hover': {
    backgroundColor: '#4b5563',
    color: '#fff',
  },
});
